import { instructionalPinyin } from './studentInstructionalPinyin';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { StudentInfoExpander, StudentTeacherNote, StudentMediaNotice, StudentLessonPicture, StudentStepNavigation } from './StudentInstructionPresentation';
import StudentPlaybackButton from './StudentPlaybackButton';
import { Alert, Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { recordStudentActivity } from './studentActivityTelemetry';
import { AudioApi } from '../api/AudioApi';
import { IVerbaScholaStudentAttemptPayload, IVerbaScholaStudentSentence } from '../api/VerbaScholaApi';
import { STUDENT_BLUE as BLUE, STUDENT_GREEN as GREEN, STUDENT_INTER as INTER, STUDENT_NAVY as NAVY, STUDENT_PANEL_RADIUS, STUDENT_POPPINS as POPPINS, STUDENT_SLATE as SLATE, STUDENT_SURFACE as SURFACE, STUDENT_SUCCESS_TEXT as SUCCESS, studentCardSx, studentEyebrowSx } from '../style/verbaScholaStudentStyles';
import { StudentStepCardProps, studentAttemptPayload } from './VerbaScholaStudentStepShared';
import { useStudentAttemptTransport } from './VerbaScholaStudentAttemptTransport';
import { chineseLang } from '../util/textLanguage';
import LessonText from './LessonText';

/** Text/image acknowledgement and required word listening share native attempt persistence. */
export default function VerbaScholaStudentInstructionCard({ step, nextAttemptNumber, onComplete, advanceLabel, review, onReviewContinue, onExit, onPrevious }: StudentStepCardProps & { advanceLabel: string; review: boolean; onReviewContinue: () => void; onExit: () => void; onPrevious?: () => void }) {
    const attemptTransport = useStudentAttemptTransport();
    const isWord = step.class_name === 'introduce_word';
    const sentence = step.sentences?.[0];
    const needsListening = Boolean(sentence);
    const [heard, setHeard] = React.useState(false);
    const [playing, setPlaying] = React.useState(false);
    const [audioError, setAudioError] = React.useState<'main' | 'example' | null>(null);
    const [playingId, setPlayingId] = React.useState<number | null>(null);
    const [loadingAudio, setLoadingAudio] = React.useState(false);
    const example = step.instructional_example;
    const content = step.instructional_content;
    const [imageError, setImageError] = React.useState(false);
    const [imageAttempt, setImageAttempt] = React.useState(0);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const alive = React.useRef(true);
    const audio = React.useRef<HTMLAudioElement | null>(null);
    const objectUrl = React.useRef<string | null>(null);
    const timer = React.useRef<ReturnType<typeof setTimeout>>();
    const request = React.useRef<IVerbaScholaStudentAttemptPayload>();
    const busy = React.useRef(false);
    const audioGeneration = React.useRef(0);
    const stop = React.useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        if (audio.current) {
            audio.current.onended = null;
            audio.current.onerror = null;
            audio.current.pause();
            audio.current = null;
        }
        if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
        objectUrl.current = null;
    }, []);
    React.useEffect(() => { alive.current = true; return () => { alive.current = false; stop(); }; }, [stop]);

    const play = async (target: IVerbaScholaStudentSentence | undefined = sentence, main = true) => {
        if (!target || playing || busy.current) return;
        setAudioError(null);
        setPlayingId(target.sentence_id);
        setPlaying(true); setLoadingAudio(true);
        const generation = ++audioGeneration.current;
        timer.current = setTimeout(() => {
            if (alive.current && generation === audioGeneration.current) {
                audioGeneration.current++; stop(); setPlaying(false); setLoadingAudio(false); setAudioError(main ? 'main' : 'example');
            }
        }, 20000);
        try {
            const response = await AudioApi.getTeacherSentence(target.sentence_id, false);
            if (!alive.current || generation !== audioGeneration.current) return;
            stop();
            objectUrl.current = URL.createObjectURL(response.data);
            const clip = new Audio(objectUrl.current);
            audio.current = clip;
            const failed = () => {
                if (!alive.current || audio.current !== clip) return;
                stop(); setPlaying(false); setLoadingAudio(false); setAudioError(main ? 'main' : 'example');
            };
            clip.onerror = failed;
            clip.onended = () => {
                if (!alive.current || audio.current !== clip) return;
                stop(); setPlaying(false); setLoadingAudio(false); if (main) setHeard(true);
            };
            timer.current = setTimeout(failed, 120000);
            await clip.play();
            if (alive.current && audio.current === clip) recordStudentActivity('model_audio_play');
            if (alive.current && audio.current === clip) setLoadingAudio(false);
        } catch {
            if (alive.current && generation === audioGeneration.current) { stop(); setPlaying(false); setLoadingAudio(false); setAudioError(main ? 'main' : 'example'); }
        }
    };
    const finish = async () => {
        if (review) { onReviewContinue(); return; }
        if (busy.current || (needsListening && !heard)) return;
        busy.current = true; setSaving(true); setSaveError(null);
        // Retry the same request after an ambiguous response; do not create duplicate attempts.
        request.current ??= { ...studentAttemptPayload(nextAttemptNumber()), content_viewed: true,
            listened_sentence_ids: needsListening && sentence ? [sentence.sentence_id] : [] };
        try {
            await attemptTransport.createAttempt(step.id, request.current, 20000);
            if (alive.current) onComplete();
        } catch {
            if (alive.current) setSaveError(attemptTransport.preview ? 'Preview could not continue. Please try again.' : 'Your progress could not be confirmed. Please try saving again.');
        } finally {
            busy.current = false;
            if (alive.current) setSaving(false);
        }
    };
    const audioButton = (target: IVerbaScholaStudentSentence, main: boolean) => <StudentPlaybackButton
        label={main ? isWord ? 'Play new word' : 'Play teacher audio' : 'Play example audio'}
        size={main && isWord ? 52 : 44} loading={playing && loadingAudio && playingId === target.sentence_id}
        playing={playing && !loadingAudio && playingId === target.sentence_id} required={main && needsListening && !heard && !review}
        disabled={playing || saving} onClick={() => play(target, main)} />;
    const picture = step.image_src_url && !imageError ? <StudentLessonPicture key={imageAttempt}
        src={step.image_src_url} alt={content?.image_alt} caption={step.title} tile={isWord} onError={() => setImageError(true)} /> : null;
    const pinyin = { fontFamily: INTER, fontSize: 13, color: SLATE, mt: .25 };
    return <Stack spacing={2}>
        <Typography sx={studentEyebrowSx}>{isWord ? 'NEW WORD' : step.image_src_url ? 'LOOK' : 'FROM YOUR TEACHER'}</Typography>
        {isWord && step.prompt_text && <StudentTeacherNote text={step.prompt_text} />}
        <Box sx={{ ...studentCardSx, overflow: 'hidden', color: NAVY }}>
            {!isWord && picture}
            <Box sx={{ p: { xs: 3, md: 3.5 } }}>
                {!isWord && step.prompt_text && <LessonText sx={{ fontFamily: INTER, color: SLATE, whiteSpace: 'pre-wrap', mb: 2 }}>{step.prompt_text}</LessonText>}
                {!isWord && step.display_pinyin && Boolean(step.prompt_pinyin?.length) && <Typography sx={pinyin}>{instructionalPinyin(step.prompt_pinyin)}</Typography>}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: isWord && example ? 'minmax(0, .8fr) minmax(0, 1.2fr)' : 'minmax(0, 1fr)' }, gap: 3.5, alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0, textAlign: isWord ? 'center' : 'left' }}>
                        {isWord && picture}
                        {isWord && <Typography lang={chineseLang(step.new_word)} sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 40, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{step.new_word}</Typography>}
                        {isWord && step.display_pinyin && Boolean(sentence?.pinyin?.length) && <Typography sx={{ ...pinyin, color: BLUE, fontWeight: 600, fontSize: 16 }}>{instructionalPinyin(sentence?.pinyin)}</Typography>}
                        {content?.english && <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE, mt: .5 }}>{content.english}</Typography>}
                        {sentence && <Stack direction="row" spacing={2} alignItems="center" justifyContent={isWord ? 'center' : 'space-between'} sx={{ mt: 2 }}>
                            {!isWord && <Box sx={{ minWidth: 0, flex: 1 }}><Typography lang={chineseLang(sentence.text)} sx={{ fontFamily: POPPINS, fontSize: 20, fontWeight: 600, overflowWrap: 'anywhere' }}>{sentence.text}</Typography>
                                {step.display_pinyin && Boolean(sentence.pinyin?.length) && <Typography sx={pinyin}>{instructionalPinyin(sentence.pinyin)}</Typography>}
                            </Box>}
                            {audioButton(sentence, true)}
                        </Stack>}
                        {needsListening && <Stack direction="row" spacing={.5} alignItems="center" justifyContent={isWord ? 'center' : 'flex-start'} sx={{ mt: 1 }}>
                            {heard && <CheckCircleRoundedIcon aria-hidden sx={{ color: GREEN, fontSize: 16 }} />}
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: heard ? SUCCESS : SLATE }}>{review ? 'Listen again any time.' : heard ? 'Heard — play it again any time' : isWord ? 'Listen to the word to continue' : 'Listen to the teacher audio to continue'}</Typography>
                        </Stack>}
                        {step.new_word_info_text && <StudentInfoExpander label={isWord ? 'About this word' : 'More info'} text={step.new_word_info_text} />}
                    </Box>
                    {example && <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ ...studentEyebrowSx, mb: 1 }}>EXAMPLE</Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: { xs: 2, md: 2.5 }, bgcolor: SURFACE, borderRadius: STUDENT_PANEL_RADIUS }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography lang={chineseLang(example.text)} sx={{ fontFamily: POPPINS, fontSize: { xs: 18, md: 20 }, fontWeight: 500, overflowWrap: 'anywhere' }}>{example.text}</Typography>
                                {step.display_pinyin && Boolean(example.pinyin?.length) && <Typography sx={pinyin}>{instructionalPinyin(example.pinyin)}</Typography>}
                                {content?.example_english && <Typography sx={pinyin}>{content.example_english}</Typography>}
                            </Box>{audioButton(example, false)}
                        </Stack>
                    </Box>}
                </Box>
                {content?.definition && <StudentInfoExpander label="Definition" text={content.definition} />}
                <Typography role="status" sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: playing ? 1 : 0 }}>{playing ? loadingAudio ? 'Loading audio…' : playingId === example?.sentence_id ? 'Playing example…' : isWord ? 'Playing the word…' : 'Playing teacher audio…' : ''}</Typography>
                {imageError && <StudentMediaNotice kind="image" onRetry={() => { setImageError(false); setImageAttempt(value => value + 1); }} onExit={onExit} />}
                {audioError && <StudentMediaNotice kind="audio" disabled={playing || saving} onRetry={() => play(audioError === 'example' ? example ?? undefined : sentence, audioError === 'main')} onExit={onExit} />}
            </Box>
        </Box>
        {saveError && <Alert severity="error">{saveError}</Alert>}
        <StudentStepNavigation onPrevious={onPrevious} onContinue={finish}
            disabled={saving || (!review && ((needsListening && !heard) || (imageError && !step.prompt_text && !sentence && !isWord)))}
            label={saving ? attemptTransport.preview ? 'Continuing…' : 'Saving…' : saveError ? attemptTransport.preview ? 'Retry preview' : 'Retry saving' : advanceLabel} />
    </Stack>;
}
